import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormRecord, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  IManagerProfile,
  ManagerProfile,
} from 'src/app/core/models/i-manager-profile';
import { WithProcessing } from 'src/app/shared/mixins/with-processing';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { ManagerService } from 'src/app/shared/services/manager.service';
import { getLanguageName } from '../shared/enums/language-helper';
import { ProfileStatus } from '../shared/enums/profile-status';
import { UserSettingsService } from 'src/app/shared/services/user-settings.service';
import { filter } from 'rxjs/operators';
import { AlertService } from 'src/app/shared/services/alert.service';
import { Observable, timer } from 'rxjs';

class ManagerProfileComponentBase {
  constructor() {}
}

const ManagerProfileComponentMixinBase = WithProcessing(
  ManagerProfileComponentBase
);

@Component({
  selector: 'app-manager-profile',
  templateUrl: './manager-profile.component.html',
  styleUrls: ['./manager-profile.component.scss'],
})
export class ManagerProfileComponent
  extends ManagerProfileComponentMixinBase
  implements OnInit
{
  constructor(
    public formBuilder: FormBuilder,
    private managerService: ManagerService,
    private dialogService: DialogService,
    private userSettingsService: UserSettingsService,
    private alertService: AlertService,
    private route: ActivatedRoute
  ) {
    super();
  }

  currentProfileId: string;
  profileName = 'Manager Profile - ';
  isSaving = false;
  isEditing = false;
  isNewProfile = false;
  createTimerDuration = 2000;
  createTimer: Observable<number> = timer(this.createTimerDuration);

  managerProfile: IManagerProfile;
  form = new FormRecord({});

  languageMap: { [key: string]: string } = {
    'en-GB': 'English (United Kingdom)',
    'en-US': 'English (United States)',
    'de-DE': 'German',
    'it-IT': 'Italian',
    'fr-FR': 'French',
    'es-ES': 'Spanish',
  };

  ngOnInit(): void {
    this.currentProfileId = this.route.snapshot.paramMap.get('id');
    this.loadManagerProfile();
  }

  loadManagerProfile(): void {
    if (!this.currentProfileId || !parseInt(this.currentProfileId)) {
      this.dialogService.openErrorDialogAndNavigate(
        {
          title: 'Manager profile not found',
          message: 'The manager profile specified was not found.',
        },
        'managers'
      );
      return;
    }
    const intManagerId = parseInt(this.currentProfileId);
    this.process$(
      this.managerService.getManagerProfileById$(intManagerId)
    ).subscribe(
      (data) => {
        if (!data || !data.managerProfile) {
          this.newManagerProfile(intManagerId);
        } else {
          this.managerProfile = data.managerProfile;
          this.profileName += this.managerProfile.name;
          Object.keys(this.managerProfile.bio).map((key) => {
            this.addTabControl(key, this.managerProfile.bio[key]);
          });
          this.isEditing = this.managerProfile.status === ProfileStatus.Editing;
        }
      },
      (error) => {
        this.dialogService.openErrorDialogAndNavigate(
          {
            title: 'Manager profile not found',
            message: 'The manager profile specified was not found.',
          },
          'managers'
        );
      }
    );
  }

  newManagerProfile(managerId: number): void {
    this.managerService.getManagerById$(managerId).subscribe(
      (data) => {
        this.profileName += data.firstName + ' ' + data.lastName;
        this.managerProfile = new ManagerProfile(
          managerId,
          data.firstName + ' ' + data.lastName,
          { 'en-GB': '' },
          new Date(),
          this.getCurrentUserName(),
          ProfileStatus.Editing
        );
        this.addTabControl('en-GB', '');
        this.isEditing = true;
        this.isNewProfile = true;
      },
      (error) => {
        this.dialogService.openErrorDialogAndNavigate(
          {
            title: 'Profile not found for this manager',
            message:
              'Error creating the manager’s profile, please contact Publishing Dev',
          },
          'managers'
        );
      }
    );
  }

  onSave(): void {
    if (this.isNewProfile) {
      this.dialogService
        .openConfirmationDialog({
          title: 'Confirm New Manager Profile',
          message:
            'You are about to create a new Manager Profile. Would you like to Proceed?',
          confirmLabel: 'Ok',
        })
        .afterClosed()
        .pipe(filter((dialogResponse) => !!dialogResponse))
        .subscribe(() => {
          this.isEditing = false;
          this.managerProfile.bio = this.form.value;
          this.managerProfile.status = ProfileStatus.Saved;
          this.managerService
            .createManagerProfile$(this.managerProfile)
            .subscribe(
              (result) => {
                if (result) {
                  this.createTimer.subscribe((x) => {
                    this.alertService.success(
                      'Manager Profile: ' +
                        this.managerProfile.name +
                        ' created successfully'
                    );
                  });
                }
                this.isSaving = false;
                this.isNewProfile = false;
              },
              () => {
                this.dialogService.openErrorDialog({
                  message:
                    'An error has occurred whilst creating manager profile. Please contact IT.',
                });
                this.managerProfile.status = ProfileStatus.Editing;
                this.isEditing = true;
                this.isSaving = false;
              }
            );
        });
    } else {
      this.isEditing = false;
      this.managerProfile.bio = this.form.value;
      this.managerProfile.status = ProfileStatus.Saved;
      // this create will be save when that endpoint gets added
      this.managerService.createManagerProfile$(this.managerProfile).subscribe(
        (result) => {
          if (result) {
            this.createTimer.subscribe((x) => {
              this.alertService.success(
                'Manager Profile: ' +
                  this.managerProfile.name +
                  ' saved successfully'
              );
            });
          }
          this.isSaving = false;
          this.isNewProfile = false;
        },
        () => {
          this.dialogService.openErrorDialog({
            message:
              'An error has occurred whilst saving manager profile. Please contact IT.',
          });
          this.managerProfile.status = ProfileStatus.Editing;
          this.isEditing = true;
          this.isSaving = false;
        }
      );
    }
  }

  getCurrentUserName(): string {
    let username = 'Unknown';
    this.userSettingsService.getCurrentUserName$().subscribe((data) => {
      username = data;
      return username;
    });
    return username;
  }

  addTabControl(label: string, content: string): void {
    this.form.addControl(
      label,
      this.formBuilder.control(content, Validators.required)
    );
  }
}
